// Projects API client (REQ-45 / K-3) — çoklu-site yönetimi (project.manage).
//
// Uçlar:
//   GET/POST    /api/v1/admin/projects
//   GET/PUT/DELETE /api/v1/admin/projects/{id}
//   GET/POST    /api/v1/admin/admin-users/{id}/projects       (kullanıcı-proje atama)
//   DELETE      /api/v1/admin/admin-users/{id}/projects/{projectId}
//
// İş kuralları BE'de: ilk proje otomatik default; tek-default invariant;
// default silinemez/unset edilemez → anlamlı hata kodları.

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import type {
  CreateProjectRequest,
  Project,
  ProjectDtoWire,
  ProjectInput,
  ProjectSummary,
  ProjectSummaryDtoWire,
  UpdateProjectRequest,
} from '@features/projects/types';

const BASE = '/api/v1/admin/projects';
const USERS_BASE = '/api/v1/admin/admin-users';

function mapProject(d: ProjectDtoWire): Project {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    slug: d.slug ?? '',
    domain: d.domain ?? null,
    isDefault: d.isDefault ?? false,
    isActive: d.isActive ?? false,
    createdAt: d.createdAt ?? '',
    updatedAt: d.updatedAt ?? '',
  };
}

function mapSummary(d: ProjectSummaryDtoWire): ProjectSummary {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    slug: d.slug ?? '',
    isDefault: d.isDefault ?? false,
  };
}

function toRequestBody(input: ProjectInput): CreateProjectRequest & UpdateProjectRequest {
  return {
    name: input.name,
    slug: input.slug,
    domain: input.domain?.trim() ? input.domain.trim() : null,
    isDefault: input.isDefault,
    isActive: input.isActive,
  };
}

export const projectsApi = {
  async list(): Promise<Project[]> {
    const res = await http.get<ActionResponse<ProjectDtoWire>>(BASE);
    const { items } = unwrapList(res);
    return items.map(mapProject);
  },

  async get(id: string): Promise<Project> {
    const res = await http.get<ActionResponse<ProjectDtoWire>>(`${BASE}/${id}`);
    return mapProject(unwrap(res));
  },

  async create(input: ProjectInput): Promise<Project> {
    const res = await http.post<ActionResponse<ProjectDtoWire>>(BASE, toRequestBody(input));
    return mapProject(unwrap(res));
  },

  async update(id: string, input: ProjectInput): Promise<Project> {
    const res = await http.put<ActionResponse<ProjectDtoWire>>(`${BASE}/${id}`, toRequestBody(input));
    return mapProject(unwrap(res));
  },

  async remove(id: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`);
    unwrap(res);
  },

  // --- Kullanıcı-proje atama (K-A) ---

  async listUserProjects(userId: string): Promise<ProjectSummary[]> {
    const res = await http.get<ActionResponse<ProjectSummaryDtoWire>>(
      `${USERS_BASE}/${userId}/projects`,
    );
    const { items } = unwrapList(res);
    return items.map(mapSummary);
  },

  async assignUserProject(userId: string, projectId: string): Promise<void> {
    const res = await http.post<ActionResponse<boolean>>(
      `${USERS_BASE}/${userId}/projects`,
      { projectId },
    );
    unwrap(res);
  },

  async removeUserProject(userId: string, projectId: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(
      `${USERS_BASE}/${userId}/projects/${projectId}`,
    );
    unwrap(res);
  },
};

export type ProjectsApi = typeof projectsApi;
